import { CANVAS_SIZE } from '@/constant';
export type PropsWithPreview<P = unknown> = P & { preview?: boolean | { answer?: boolean } } & { interactive?: boolean };

export function isCoordinate(coordinate: unknown): coordinate is Coordinate {
    return (
        (coordinate as Coordinate).x !== undefined && typeof (coordinate as Coordinate).x === 'number' && (coordinate as Coordinate).y !== undefined && typeof (coordinate as Coordinate).y === 'number'
    );
}

export type Coordinate = {
    x: number;
    y: number;
};

export function isSize(size: unknown): size is Size {
    return (size as Size).width !== undefined && typeof (size as Size).width === 'number' && (size as Size).height !== undefined && typeof (size as Size).height === 'number';
}

export type Size = {
    width: number;
    height: number;
};

/**
 * Converts an array of stroke to an SVG path data string
 * @param stroke - A stroke with an array of x and y coordinates
 * @param scale - scaling factor of the coordinates
 * @param origin - origin of the coordinates of the strokes
 * @returns SVG path "d" attribute string
 */
export function Stroke2Path(stroke: Stroke, scale: number, origin: Coordinate = { x: 0, y: 0 }): string {
    if (stroke.length === 0) return '';
    const [first, ...rest] = stroke;
    if (!first) return '';
    if (stroke.length === 1) return `M${first.x * scale + origin.x} ${first.y * scale + origin.y} L${first.x * scale + origin.x} ${first.y * scale + origin.y}`;

    const moveTo = `M${first.x * scale + origin.x} ${first.y * scale + origin.y}`;
    const lineTos = rest.map((p) => `L${p.x * scale + origin.x} ${p.y * scale + origin.y}`).join(' ');

    return `${moveTo} ${lineTos}`;
}

/**
 * Question
 */

export function isStroke(data: unknown): data is Stroke {
    return Array.isArray(data) && data.every(isCoordinate);
}

export function isStrokeHistory(data: unknown): data is StrokeHistory {
    return Array.isArray(data) && data.every(isStroke);
}

export function isWritingResult(data: unknown): data is WritingResult {
    return (
        (data as WritingResult).writing !== undefined &&
        (data as WritingResult).writing.character !== undefined &&
        typeof (data as WritingResult).writing.character === 'string' &&
        (data as WritingResult).writing.normalMode !== undefined &&
        typeof (data as WritingResult).writing.normalMode === 'boolean' &&
        (data as WritingResult).writing.strokes !== undefined &&
        (data as WritingResult).writing.strokes !== null &&
        ((data as WritingResult).writing.normalMode === true
            ? Array.isArray((data as WritingResult).writing.strokes) && (data as WritingResult).writing.strokes.every(isStrokeHistory)
            : Array.isArray((data as WritingResult).writing.strokes) && (data as WritingResult).writing.strokes.every(isStroke))
    );
}

export type Stroke = Coordinate[];

type StrokeHistory = Stroke[];

export type WritingStrokes =
    | {
          normalMode: true;
          strokes: StrokeHistory[];
      }
    | {
          normalMode: false;
          strokes: Stroke[];
      };

export interface WritingResult {
    writing: {
        character: string;
        version?: number;
    } & WritingStrokes;
}

export function isEnWritingResult(data: unknown): data is EnWritingResult {
    return (
        (data as EnWritingResult).phrase !== undefined &&
        typeof (data as EnWritingResult).phrase === 'string' &&
        (data as EnWritingResult).writing !== undefined &&
        Array.isArray((data as EnWritingResult).writing) &&
        (data as EnWritingResult).writing.every(
            (item) =>
                item !== undefined &&
                item.normalMode !== undefined &&
                typeof item.normalMode === 'boolean' &&
                item.width !== undefined &&
                typeof item.width === 'number' &&
                item.height !== undefined &&
                typeof item.height === 'number' &&
                item.strokes !== undefined &&
                item.strokes !== null &&
                Array.isArray(item.strokes) &&
                (item.normalMode ? item.strokes.every(isStrokeHistory) : item.strokes.every(isStroke)),
        )
    );
}
export interface EnWritingResult {
    phrase: string;
    writing: Array<
        {
            version?: number;
            width: number;
            height: number;
        } & WritingStrokes
    >;
}
export function isCardResult(data: unknown): data is CardResult {
    return (
        (data as CardResult).card !== undefined &&
        (data as CardResult).card.detectedCard1 !== undefined &&
        ((data as CardResult).card.detectedCard1.serial === undefined || typeof (data as CardResult).card.detectedCard1.serial === 'string') &&
        ((data as CardResult).card.detectedCard1.orientation === undefined || typeof (data as CardResult).card.detectedCard1.orientation === 'string') &&
        (data as CardResult).card.detectedCard2 !== undefined &&
        ((data as CardResult).card.detectedCard2.serial === undefined || typeof (data as CardResult).card.detectedCard2.serial === 'string') &&
        ((data as CardResult).card.detectedCard2.orientation === undefined || typeof (data as CardResult).card.detectedCard2.orientation === 'string')
    );
}

export type CardOrientation = 'Left' | 'Right' | 'Up' | 'Down';

export interface CardResult {
    card: {
        detectedCard1: { serial?: string; orientation?: CardOrientation };
        detectedCard2: { serial?: string; orientation?: CardOrientation };
    };
}

export function isDndResult(data: unknown): data is DndResult {
    return (
        (data as DndResult).dnd !== undefined &&
        (data as DndResult).dnd.data !== undefined &&
        Array.isArray((data as DndResult).dnd.data) &&
        (data as DndResult).dnd.data.every(
            (item) => item.content !== undefined && isSlideComponentDndDragZone(item.content) && (item.droppedComponentId === undefined || typeof item.droppedComponentId === 'string'),
        )
    );
}

export interface DndResult {
    dnd: {
        data: {
            content: SlideComponentDndDragZone;
            droppedComponentId: string;
        }[];
    };
}

export interface Result {
    id: string;
    message: string;
    data: string;
    startTime: string;
    endTime: string;
    componentId: string;
    correct: boolean;
    grading: string;
    gameType: string;
    fetchResultFiles?: () => Promise<
        {
            url: string;
        }[]
    >;
}

// Question slide component with answers
export type Question = SlideComponent & {
    answers: Result[];
};

export type QuestionSlide = Slide & {
    questions: Question[];
    score: number;
};

/**
 * end of Question
 */

/**
 * Slide
 */

export enum SlideTemplateType {
    blank = 'blank',
}

export type Slide = {
    id: string;
    sort: number;
    data: SlideComponent[];
    questionCount?: number;
};

export function parseSlide(value: Pick<Slide, 'id' | 'sort' | 'questionCount'> & { data?: unknown }): Slide {
    if (typeof value.data === 'string' && value.data) {
        const data = JSON.parse(value.data);
        const components = Array.isArray(data) ? data.filter(isSlideComponent) : [];
        return {
            ...value,
            data: components,
        };
    } else {
        return { ...value, data: [] };
    }
}

export function isSlide(slide: unknown): slide is Slide {
    return (
        (slide as Slide).id !== undefined &&
        typeof (slide as Slide).id === 'string' &&
        (slide as Slide).sort !== undefined &&
        typeof (slide as Slide).sort === 'number' &&
        Array.isArray((slide as Slide).data) &&
        (slide as Slide).data.every((component) => isSlideComponent(component))
    );
}

const staticAssetComponentType = ['text', 'image', 'background', 'audio', 'link'] as const;
const questionComponentType = ['dnd', 'characterWriting', 'characterCards', 'drawing', 'audioRecord', 'pronunciationAssessment', 'strokeWriting', 'radicalWriting', 'mc', 'sentenceMaker'] as const;
export const legacyQuestionComponentType = [
    'character',
    /**
     * HKCS legacy components
     */
    'hkcsQ1',
    'hkcsQ2',
    'hkcsQ3',
    'hkcsEnQ1',
    'hkcsEnQ2',
] as const;

const sasLegacyQuestionComponentType = [
    /**
     * SAS legacy components
     */
    'sasAudio', // deprecated
    'sasFillInTheBlanks', // deprecated
    'sasFillInTheBlanksWriting', // deprecated
    'sasRadicalMatching', // deprecated
    'multipleChoice', // deprecated
] as const;
const slideComponentType = [...staticAssetComponentType, ...questionComponentType, ...legacyQuestionComponentType, ...sasLegacyQuestionComponentType] as const;
type SlideComponentType = (typeof slideComponentType)[number];

export interface SlideComponent {
    id: string;
    type: SlideComponentType;
    pos: Coordinate;
    size: Size;
    content?: unknown;
}

export function isSlideComponent(component: unknown): component is SlideComponent {
    return (
        (component as SlideComponent).id !== undefined &&
        slideComponentType.includes((component as SlideComponent).type) &&
        (component as SlideComponent).pos !== undefined &&
        isCoordinate((component as SlideComponent).pos) &&
        (component as SlideComponent).size !== undefined &&
        isSize((component as SlideComponent).size)
    );
}

export function reposition<T extends { pos: Coordinate; size: Size }>(model: T, adjustSize: boolean = true) {
    const rightEdge = model.pos.x + model.size.width;
    const bottomEdge = model.pos.y + model.size.height;

    if (rightEdge > CANVAS_SIZE.width || bottomEdge > CANVAS_SIZE.height || model.size.width > CANVAS_SIZE.width || model.size.height > CANVAS_SIZE.height) {
        if (adjustSize) {
            // Calculate ratios considering both position and size
            const widthRatio = (CANVAS_SIZE.width - model.pos.x) / model.size.width;
            const heightRatio = (CANVAS_SIZE.height - model.pos.y) / model.size.height;
            const ratio = Math.min(widthRatio, heightRatio);

            // Adjust size while maintaining aspect ratio
            model.size = {
                width: Math.floor(model.size.width * ratio),
                height: Math.floor(model.size.height * ratio),
            };
        }

        // If still exceeds after scaling, adjust re-position
        if (model.pos.x + model.size.width > CANVAS_SIZE.width) {
            model.pos.x = CANVAS_SIZE.width - model.size.width;
        }
        if (model.pos.y + model.size.height > CANVAS_SIZE.height) {
            model.pos.y = CANVAS_SIZE.height - model.size.height;
        }
    }

    return model;
}

/**
 * end of Slide
 */

/**
 * Text
 */

export interface SlideComponentText extends SlideComponent {
    content: {
        text: string;
        jyutping: [string, string | null][];
        fontSize: number;
        color: string;
        typeSettings: {
            isPhonetic: boolean;
            fontFamily: string;
            formatting: ('bold' | 'italic' | 'underlined')[];
            alignment: 'left' | 'center' | 'right';
            borderRadius: number;
            backgroundColor: string;
        };
    };
}

export function isSlideComponentText(component: unknown): component is SlideComponentText {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'text' &&
        (component as SlideComponentText).content !== undefined &&
        (component as SlideComponentText).content.text !== undefined &&
        typeof (component as SlideComponentText).content.text === 'string' &&
        (component as SlideComponentText).content.jyutping !== undefined &&
        Array.isArray((component as SlideComponentText).content.jyutping) &&
        (component as SlideComponentText).content.jyutping.every(
            (jyutping) => Array.isArray(jyutping) && typeof jyutping[0] === 'string' && (jyutping[1] === null || typeof jyutping[1] === 'string'),
        ) &&
        (component as SlideComponentText).content.fontSize !== undefined &&
        typeof (component as SlideComponentText).content.fontSize === 'number'
    );
}

/**
 * end of Text
 */

/**
 * Background
 */

export interface SlideComponentBackground extends SlideComponent {
    content: {
        filename: string;
        imageURL: string;
        flip: number;
    };
}

export function isSlideComponentBackground(component: unknown): component is SlideComponentBackground {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'background' &&
        (component as SlideComponentBackground).content !== undefined &&
        (component as SlideComponentBackground).content.filename !== undefined &&
        typeof (component as SlideComponentBackground).content.filename === 'string' &&
        (component as SlideComponentBackground).content.imageURL !== undefined &&
        typeof (component as SlideComponentBackground).content.imageURL === 'string' &&
        (component as SlideComponentBackground).content.flip !== undefined &&
        typeof (component as SlideComponentBackground).content.flip === 'number'
    );
}
/**
 * end of Background
 */

/**
 * Image
 */
export interface SlideComponentImage extends SlideComponent {
    content: {
        image: string;
        imageURL: string;
        flip: number;
    };
}

export function isSlideComponentImage(component: unknown): component is SlideComponentImage {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'image' &&
        (component as SlideComponentImage).content !== undefined &&
        (component as SlideComponentImage).content.image !== undefined &&
        typeof (component as SlideComponentImage).content.image === 'string' &&
        (component as SlideComponentImage).content.imageURL !== undefined &&
        typeof (component as SlideComponentImage).content.imageURL === 'string' &&
        (component as SlideComponentImage).content.flip !== undefined &&
        typeof (component as SlideComponentImage).content.flip === 'number'
    );
}

/**
 * end of Image
 */

/**
 * Audio
 */

export type SlideComponentFileModel = {
    id: string;
    filename: string;
    url: string;
    ai?: {
        voice: string;
        text: string;
    };
};

export interface SlideComponentAudio extends SlideComponent {
    content: {
        audio: SlideComponentFileModel[];
    };
}

export function isSlideComponentFileModel(fileModel: unknown): fileModel is SlideComponentFileModel {
    return (
        (fileModel as SlideComponentFileModel).id !== undefined &&
        typeof (fileModel as SlideComponentFileModel).id === 'string' &&
        (fileModel as SlideComponentFileModel).filename !== undefined &&
        typeof (fileModel as SlideComponentFileModel).filename === 'string' &&
        (fileModel as SlideComponentFileModel).url !== undefined &&
        typeof (fileModel as SlideComponentFileModel).url === 'string'
    );
}

export function isSlideComponentAudio(component: unknown): component is SlideComponentAudio {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'audio' &&
        (component as SlideComponentAudio).content !== undefined &&
        (component as SlideComponentAudio).content.audio !== undefined &&
        Array.isArray((component as SlideComponentAudio).content.audio) &&
        (component as SlideComponentAudio).content.audio.every((audio) => isSlideComponentFileModel(audio))
    );
}

/**
 * end of Audio
 */

/**
 * Character (Legacy)
 */

export type SlideComponentCharacterModel = {
    id: string;
    code?: string;
    locale?: string;
    character: string;
    jyutping: { label: string; label2?: string; sound: string }[];
    pinyin: { label: string; label2?: string; sound: string }[];
    serial1: string[];
    serial2: string[];
    cardType: number;
};

export function isSlideComponentCharacterModel(component: unknown): component is SlideComponentCharacterModel {
    return (
        (component as SlideComponentCharacterModel).id !== undefined &&
        typeof (component as SlideComponentCharacterModel).id === 'string' &&
        ((component as SlideComponentCharacterModel).code === undefined || typeof (component as SlideComponentCharacterModel).code === 'string') &&
        ((component as SlideComponentCharacterModel).locale === undefined || typeof (component as SlideComponentCharacterModel).locale === 'string') &&
        (component as SlideComponentCharacterModel).character !== undefined &&
        typeof (component as SlideComponentCharacterModel).character === 'string' &&
        (component as SlideComponentCharacterModel).jyutping !== undefined &&
        Array.isArray((component as SlideComponentCharacterModel).jyutping) &&
        (component as SlideComponentCharacterModel).jyutping.every(
            (jyutping) => jyutping !== undefined && jyutping.label !== undefined && typeof jyutping.label === 'string' && jyutping.sound !== undefined && typeof jyutping.sound === 'string',
        ) &&
        (component as SlideComponentCharacterModel).pinyin !== undefined &&
        Array.isArray((component as SlideComponentCharacterModel).pinyin) &&
        (component as SlideComponentCharacterModel).pinyin.every(
            (pinyin) => pinyin !== undefined && pinyin.label !== undefined && typeof pinyin.label === 'string' && pinyin.sound !== undefined && typeof pinyin.sound === 'string',
        ) &&
        (component as SlideComponentCharacterModel).serial1 !== undefined &&
        Array.isArray((component as SlideComponentCharacterModel).serial1) &&
        (component as SlideComponentCharacterModel).serial1.every((serial1) => typeof serial1 === 'string') &&
        (component as SlideComponentCharacterModel).serial2 !== undefined &&
        Array.isArray((component as SlideComponentCharacterModel).serial2) &&
        (component as SlideComponentCharacterModel).serial2.every((serial2) => typeof serial2 === 'string') &&
        (component as SlideComponentCharacterModel).cardType !== undefined &&
        typeof (component as SlideComponentCharacterModel).cardType === 'number'
    );
}

export type SlideComponentCharacterSoundModel = {
    jyutping: { label: string; label2?: string; sound: string }[];
    pinyin: { label: string; label2?: string; sound: string }[];
};

export function isSlideComponentCharacterSoundModel(component: unknown): component is SlideComponentCharacterSoundModel {
    return (
        (component as SlideComponentCharacterSoundModel).jyutping !== undefined &&
        Array.isArray((component as SlideComponentCharacterSoundModel).jyutping) &&
        (component as SlideComponentCharacterSoundModel).jyutping.every(
            (jyutping) => jyutping !== undefined && jyutping.label !== undefined && typeof jyutping.label === 'string' && jyutping.sound !== undefined && typeof jyutping.sound === 'string',
        ) &&
        (component as SlideComponentCharacterSoundModel).pinyin !== undefined &&
        Array.isArray((component as SlideComponentCharacterSoundModel).pinyin) &&
        (component as SlideComponentCharacterSoundModel).pinyin.every(
            (pinyin) => pinyin !== undefined && pinyin.label !== undefined && typeof pinyin.label === 'string' && pinyin.sound !== undefined && typeof pinyin.sound === 'string',
        )
    );
}

export type SlideComponentCharacterSoundGroupModel = {
    jyutping: { startIndex: number }[];
    pinyin: { startIndex: number }[];
};

export function isSlideComponentCharacterSoundGroupModel(component: unknown): component is SlideComponentCharacterSoundGroupModel {
    return (
        (component as SlideComponentCharacterSoundGroupModel).jyutping !== undefined &&
        Array.isArray((component as SlideComponentCharacterSoundGroupModel).jyutping) &&
        (component as SlideComponentCharacterSoundGroupModel).jyutping.every((jyutping) => jyutping !== undefined && jyutping.startIndex !== undefined && typeof jyutping.startIndex === 'number') &&
        (component as SlideComponentCharacterSoundGroupModel).pinyin !== undefined &&
        Array.isArray((component as SlideComponentCharacterSoundGroupModel).pinyin) &&
        (component as SlideComponentCharacterSoundGroupModel).pinyin.every((pinyin) => pinyin !== undefined && pinyin.startIndex !== undefined && typeof pinyin.startIndex === 'number')
    );
}

export type GameType = 'OneCard' | 'TwoCards' | 'Writing' | 'Audio';

export interface SlideComponentCharacter extends SlideComponent {
    content: {
        image: string;
        characterList: SlideComponentCharacterModel[];
        character: number;
        characterSound: SlideComponentCharacterSoundModel;
        phrase: string;
        phraseSound: SlideComponentCharacterSoundModel;
        phraseSoundGroup?: SlideComponentCharacterSoundGroupModel;
        imageURL: string;
        gameType: GameType;
        flip: number;
        version?: number;
        normalMode?: boolean;
    };
}

export function isSlideComponentCharacter(component: unknown): component is SlideComponentCharacter {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'character' &&
        (component as SlideComponentCharacter).content !== undefined &&
        (component as SlideComponentCharacter).content.image !== undefined &&
        typeof (component as SlideComponentCharacter).content.image === 'string' &&
        (component as SlideComponentCharacter).content.character !== undefined &&
        typeof (component as SlideComponentCharacter).content.character === 'number' &&
        (component as SlideComponentCharacter).content.characterSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentCharacter).content.characterSound) &&
        (component as SlideComponentCharacter).content.phrase !== undefined &&
        typeof (component as SlideComponentCharacter).content.phrase === 'string' &&
        (component as SlideComponentCharacter).content.phraseSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentCharacter).content.phraseSound) &&
        ((component as SlideComponentCharacter).content.phraseSoundGroup === undefined || isSlideComponentCharacterSoundGroupModel((component as SlideComponentCharacter).content.phraseSoundGroup)) &&
        (component as SlideComponentCharacter).content.imageURL !== undefined &&
        typeof (component as SlideComponentCharacter).content.imageURL === 'string' &&
        (component as SlideComponentCharacter).content.gameType !== undefined &&
        ((component as SlideComponentCharacter).content.gameType === 'OneCard' ||
            (component as SlideComponentCharacter).content.gameType === 'TwoCards' ||
            (component as SlideComponentCharacter).content.gameType === 'Writing' ||
            (component as SlideComponentCharacter).content.gameType === 'Audio') &&
        (component as SlideComponentCharacter).content.flip !== undefined &&
        typeof (component as SlideComponentCharacter).content.flip === 'number' &&
        ((component as SlideComponentCharacter).content.version === undefined || typeof (component as SlideComponentCharacter).content.version === 'number') &&
        ((component as SlideComponentCharacter).content.normalMode === undefined || typeof (component as SlideComponentCharacter).content.normalMode === 'boolean')
    );
}

/**
 * end of Character (Legacy)
 */

/**
 * HkcsQ1 (Legacy)
 */

export type SlideComponentHkcsQ1QuestionModel = {
    id: string;
    image: string;
    imageURL: string;
    flip: number;
    sort: number;
};

export function isSlideComponentHkcsQ1QuestionModel(component: unknown): component is SlideComponentHkcsQ1QuestionModel {
    return (
        (component as SlideComponentHkcsQ1QuestionModel).id !== undefined &&
        typeof (component as SlideComponentHkcsQ1QuestionModel).id === 'string' &&
        ((component as SlideComponentHkcsQ1QuestionModel).image === undefined || typeof (component as SlideComponentHkcsQ1QuestionModel).image === 'string') &&
        ((component as SlideComponentHkcsQ1QuestionModel).imageURL === undefined || typeof (component as SlideComponentHkcsQ1QuestionModel).imageURL === 'string') &&
        (component as SlideComponentHkcsQ1QuestionModel).flip !== undefined &&
        typeof (component as SlideComponentHkcsQ1QuestionModel).flip === 'number' &&
        (component as SlideComponentHkcsQ1QuestionModel).sort !== undefined &&
        typeof (component as SlideComponentHkcsQ1QuestionModel).sort === 'number'
    );
}

export interface SlideComponentHkcsQ1 extends SlideComponent {
    type: 'hkcsQ1';
    content: {
        characterList: SlideComponentCharacterModel[];
        character: number;
        characterSound: SlideComponentCharacterSoundModel;
        phrase: string;
        phraseSound: SlideComponentCharacterSoundModel;
        phraseSoundGroup: SlideComponentCharacterSoundGroupModel;
        image1: string;
        image1URL: string;
        keyword1: string;
        image2: string;
        image2URL: string;
        keyword2: string;
        questionCount: number;
        questions: SlideComponentHkcsQ1QuestionModel[];
    };
}

export function isSlideComponentHkcsQ1(component: unknown): component is SlideComponentHkcsQ1 {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'hkcsQ1' &&
        (component as SlideComponentHkcsQ1).content !== undefined &&
        (component as SlideComponentHkcsQ1).content.character !== undefined &&
        typeof (component as SlideComponentHkcsQ1).content.character === 'number' &&
        (component as SlideComponentHkcsQ1).content.characterSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentHkcsQ1).content.characterSound) &&
        (component as SlideComponentHkcsQ1).content.phrase !== undefined &&
        typeof (component as SlideComponentHkcsQ1).content.phrase === 'string' &&
        (component as SlideComponentHkcsQ1).content.phraseSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentHkcsQ1).content.phraseSound) &&
        ((component as SlideComponentHkcsQ1).content.phraseSoundGroup === undefined || isSlideComponentCharacterSoundGroupModel((component as SlideComponentHkcsQ1).content.phraseSoundGroup)) &&
        (component as SlideComponentHkcsQ1).content.image1 !== undefined &&
        typeof (component as SlideComponentHkcsQ1).content.image1 === 'string' &&
        (component as SlideComponentHkcsQ1).content.image1URL !== undefined &&
        typeof (component as SlideComponentHkcsQ1).content.image1URL === 'string' &&
        (component as SlideComponentHkcsQ1).content.keyword1 !== undefined &&
        typeof (component as SlideComponentHkcsQ1).content.keyword1 === 'string' &&
        (component as SlideComponentHkcsQ1).content.image2 !== undefined &&
        typeof (component as SlideComponentHkcsQ1).content.image2 === 'string' &&
        (component as SlideComponentHkcsQ1).content.image2URL !== undefined &&
        typeof (component as SlideComponentHkcsQ1).content.image2URL === 'string' &&
        (component as SlideComponentHkcsQ1).content.keyword2 !== undefined &&
        typeof (component as SlideComponentHkcsQ1).content.keyword2 === 'string' &&
        (component as SlideComponentHkcsQ1).content.questionCount !== undefined &&
        typeof (component as SlideComponentHkcsQ1).content.questionCount === 'number'
    );
}

/**
 * end of HkcsQ1 (Legacy)
 */

/**
 * HkcsQ2 (Legacy)
 */

export interface SlideComponentHkcsQ2 extends SlideComponent {
    type: 'hkcsQ2';
    content: {
        characterList: SlideComponentCharacterModel[];
        character: number;
        characterSound: SlideComponentCharacterSoundModel;
        phrase: string;
        phraseSound: SlideComponentCharacterSoundModel;
        phraseSoundGroup: SlideComponentCharacterSoundGroupModel;
        image: string;
        imageURL: string;
        flip: number;
    };
}

export function isSlideComponentHkcsQ2(component: unknown): component is SlideComponentHkcsQ2 {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'hkcsQ2' &&
        (component as SlideComponentHkcsQ2).content !== undefined &&
        (component as SlideComponentHkcsQ2).content.character !== undefined &&
        typeof (component as SlideComponentHkcsQ2).content.character === 'number' &&
        (component as SlideComponentHkcsQ2).content.characterSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentHkcsQ2).content.characterSound) &&
        (component as SlideComponentHkcsQ2).content.phrase !== undefined &&
        typeof (component as SlideComponentHkcsQ2).content.phrase === 'string' &&
        (component as SlideComponentHkcsQ2).content.phraseSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentHkcsQ2).content.phraseSound) &&
        ((component as SlideComponentHkcsQ2).content.phraseSoundGroup === undefined || isSlideComponentCharacterSoundGroupModel((component as SlideComponentHkcsQ2).content.phraseSoundGroup)) &&
        (component as SlideComponentHkcsQ2).content.image !== undefined &&
        typeof (component as SlideComponentHkcsQ2).content.image === 'string' &&
        (component as SlideComponentHkcsQ2).content.imageURL !== undefined &&
        typeof (component as SlideComponentHkcsQ2).content.imageURL === 'string' &&
        (component as SlideComponentHkcsQ2).content.flip !== undefined &&
        typeof (component as SlideComponentHkcsQ2).content.flip === 'number'
    );
}

/**
 * end of HkcsQ2 (Legacy)
 */

/**
 * HkcsQ3 (Legacy)
 */

export type SlideComponentHkcsQ3CharacterOptionModel = {
    characterList: SlideComponentCharacterModel[];
    character: number;
    characterSound: SlideComponentCharacterSoundModel;
    phrase: string;
    phraseSound: SlideComponentCharacterSoundModel;
    phraseSoundGroup: SlideComponentCharacterSoundGroupModel;
    isCorrect: boolean;
};

export interface SlideComponentHkcsQ3 extends SlideComponent {
    content: {
        characterList: SlideComponentCharacterModel[];
        character: number;
        characterSound: SlideComponentCharacterSoundModel;
        phrase: string;
        phraseSound: SlideComponentCharacterSoundModel;
        phraseSoundGroup: SlideComponentCharacterSoundGroupModel;
        image: string;
        imageURL: string;
        flip: number;
        characterOptions: SlideComponentHkcsQ3CharacterOptionModel[];
    };
}

export function isSlideComponentHkcsQ3(component: unknown): component is SlideComponentHkcsQ3 {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'hkcsQ3' &&
        (component as SlideComponentHkcsQ3).content !== undefined &&
        (component as SlideComponentHkcsQ3).content.character !== undefined &&
        typeof (component as SlideComponentHkcsQ3).content.character === 'number' &&
        (component as SlideComponentHkcsQ3).content.characterSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentHkcsQ3).content.characterSound) &&
        (component as SlideComponentHkcsQ3).content.phrase !== undefined &&
        typeof (component as SlideComponentHkcsQ3).content.phrase === 'string' &&
        (component as SlideComponentHkcsQ3).content.phraseSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentHkcsQ3).content.phraseSound) &&
        ((component as SlideComponentHkcsQ3).content.phraseSoundGroup === undefined || isSlideComponentCharacterSoundGroupModel((component as SlideComponentHkcsQ3).content.phraseSoundGroup)) &&
        (component as SlideComponentHkcsQ3).content.image !== undefined &&
        typeof (component as SlideComponentHkcsQ3).content.image === 'string' &&
        (component as SlideComponentHkcsQ3).content.imageURL !== undefined &&
        typeof (component as SlideComponentHkcsQ3).content.imageURL === 'string' &&
        (component as SlideComponentHkcsQ3).content.flip !== undefined &&
        typeof (component as SlideComponentHkcsQ3).content.flip === 'number'
    );
}

/**
 * end of HkcsQ3 (Legacy)
 */

/**
 * HkcsEnQ1 (Legacy)
 */

export interface SlideComponentHkcsEnQ1 extends SlideComponent {
    content: {
        phrase: string;
        image: string;
        imageURL: string;
        flip: number;
        audio: SlideComponentFileModel[];
    };
}

export function isSlideComponentHkcsEnQ1(component: unknown): component is SlideComponentHkcsEnQ1 {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'hkcsEnQ1' &&
        (component as SlideComponentHkcsEnQ1).content !== undefined &&
        (component as SlideComponentHkcsEnQ1).content.phrase !== undefined &&
        typeof (component as SlideComponentHkcsEnQ1).content.phrase === 'string' &&
        (component as SlideComponentHkcsEnQ1).content.image !== undefined &&
        typeof (component as SlideComponentHkcsEnQ1).content.image === 'string' &&
        (component as SlideComponentHkcsEnQ1).content.imageURL !== undefined &&
        typeof (component as SlideComponentHkcsEnQ1).content.imageURL === 'string' &&
        (component as SlideComponentHkcsEnQ1).content.flip !== undefined &&
        typeof (component as SlideComponentHkcsEnQ1).content.flip === 'number'
    );
}

/**
 * end of HkcsEnQ1 (Legacy)
 */

/**
 * HkcsEnQ2 (Legacy)
 */

export interface SlideComponentHkcsEnQ2 extends SlideComponent {
    content: {
        phrase: string;
        sampleSentence: string;
        image: string;
        imageURL: string;
        flip: number;
        audio: SlideComponentFileModel[];
    };
}

export function isSlideComponentHkcsEnQ2(component: unknown): component is SlideComponentHkcsEnQ2 {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'hkcsEnQ2' &&
        (component as SlideComponentHkcsEnQ2).content !== undefined &&
        (component as SlideComponentHkcsEnQ2).content.phrase !== undefined &&
        typeof (component as SlideComponentHkcsEnQ2).content.phrase === 'string' &&
        (component as SlideComponentHkcsEnQ2).content.sampleSentence !== undefined &&
        typeof (component as SlideComponentHkcsEnQ2).content.sampleSentence === 'string' &&
        (component as SlideComponentHkcsEnQ2).content.image !== undefined &&
        typeof (component as SlideComponentHkcsEnQ2).content.image === 'string' &&
        (component as SlideComponentHkcsEnQ2).content.imageURL !== undefined &&
        typeof (component as SlideComponentHkcsEnQ2).content.imageURL === 'string' &&
        (component as SlideComponentHkcsEnQ2).content.flip !== undefined &&
        typeof (component as SlideComponentHkcsEnQ2).content.flip === 'number'
    );
}

/**
 * end of HkcsEnQ2 (Legacy)
 */

/**
 * SasAudio (Legacy)
 */

export interface SlideComponentSasAudio extends SlideComponent {
    content: {
        exampleImage: string;
        exampleImageURL: string;
        exampleText1: string;
        exampleText2: string;
        exampleImageFlip: number;
        questionImage: string;
        questionImageURL: string;
        questionText: string;
        questionImageFlip: number;
    };
}

export function isSlideComponentSasAudio(component: unknown): component is SlideComponentSasAudio {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'sasAudio' &&
        (component as SlideComponentSasAudio).content !== undefined &&
        (component as SlideComponentSasAudio).content.exampleImage !== undefined &&
        typeof (component as SlideComponentSasAudio).content.exampleImage === 'string' &&
        (component as SlideComponentSasAudio).content.exampleImageURL !== undefined &&
        typeof (component as SlideComponentSasAudio).content.exampleImageURL === 'string' &&
        (component as SlideComponentSasAudio).content.exampleText1 !== undefined &&
        typeof (component as SlideComponentSasAudio).content.exampleText1 === 'string' &&
        (component as SlideComponentSasAudio).content.exampleText2 !== undefined &&
        typeof (component as SlideComponentSasAudio).content.exampleText2 === 'string' &&
        (component as SlideComponentSasAudio).content.exampleImageFlip !== undefined &&
        typeof (component as SlideComponentSasAudio).content.exampleImageFlip === 'number' &&
        (component as SlideComponentSasAudio).content.questionImage !== undefined &&
        typeof (component as SlideComponentSasAudio).content.questionImage === 'string' &&
        (component as SlideComponentSasAudio).content.questionImageURL !== undefined &&
        typeof (component as SlideComponentSasAudio).content.questionImageURL === 'string' &&
        (component as SlideComponentSasAudio).content.questionText !== undefined &&
        typeof (component as SlideComponentSasAudio).content.questionText === 'string' &&
        (component as SlideComponentSasAudio).content.questionImageFlip !== undefined &&
        typeof (component as SlideComponentSasAudio).content.questionImageFlip === 'number'
    );
}

/**
 * end of SasAudio (Legacy)
 */

/**
 * SasFillInTheBlanks (Legacy)
 */

export type SlideComponentSasFillInTheBlanksQuestionModels = {
    id: string;
    questionText: string;
    sort: number;
};

export type SlideComponentSasFillInTheBlanksAnswerModels = {
    answerText: string;
    sort: number;
};

export interface SlideComponentSasFillInTheBlanks extends SlideComponent {
    content: {
        image: string;
        imageURL: string;
        flip: number;
        instructionText: string;
        questions: SlideComponentSasFillInTheBlanksQuestionModels[];
        answers: SlideComponentSasFillInTheBlanksAnswerModels[];
        extraAnswers: string[];
    };
}

export function isSlideComponentSasFillInTheBlanks(component: unknown): component is SlideComponentSasFillInTheBlanks {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'sasFillInTheBlanks' &&
        (component as SlideComponentSasFillInTheBlanks).content !== undefined &&
        (component as SlideComponentSasFillInTheBlanks).content.image !== undefined &&
        typeof (component as SlideComponentSasFillInTheBlanks).content.image === 'string' &&
        (component as SlideComponentSasFillInTheBlanks).content.imageURL !== undefined &&
        typeof (component as SlideComponentSasFillInTheBlanks).content.imageURL === 'string' &&
        (component as SlideComponentSasFillInTheBlanks).content.instructionText !== undefined &&
        typeof (component as SlideComponentSasFillInTheBlanks).content.instructionText === 'string' &&
        (component as SlideComponentSasFillInTheBlanks).content.extraAnswers !== undefined &&
        Array.isArray((component as SlideComponentSasFillInTheBlanks).content.extraAnswers) &&
        (component as SlideComponentSasFillInTheBlanks).content.extraAnswers.every((extraAnswer) => typeof extraAnswer === 'string') &&
        (component as SlideComponentSasFillInTheBlanks).content.flip !== undefined &&
        typeof (component as SlideComponentSasFillInTheBlanks).content.flip === 'number'
    );
}

/**
 * end of SasFillInTheBlanks (Legacy)
 */

/**
 * SasFillInTheBlanksWriting (Legacy)
 */

export type SlideComponentSasFillInTheBlanksWritingQuestionModels = {
    id: string;
    questionText: string;
    sort: number;
};

export type SlideComponentSasFillInTheBlanksWritingAnswerModels = {
    answerText: string;
    sort: number;
};

export interface SlideComponentSasFillInTheBlanksWriting extends SlideComponent {
    content: {
        image: string;
        imageURL: string;
        flip: number;
        locale: string;
        instructionText: string;
        questions: SlideComponentSasFillInTheBlanksWritingQuestionModels[];
        answers: SlideComponentSasFillInTheBlanksWritingAnswerModels[];
        extraAnswers: string[];
    };
}

export function isSlideComponentSasFillInTheBlanksWriting(component: unknown): component is SlideComponentSasFillInTheBlanksWriting {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'sasFillInTheBlanksWriting' &&
        (component as SlideComponentSasFillInTheBlanksWriting).content !== undefined &&
        (component as SlideComponentSasFillInTheBlanksWriting).content.image !== undefined &&
        typeof (component as SlideComponentSasFillInTheBlanksWriting).content.image === 'string' &&
        (component as SlideComponentSasFillInTheBlanksWriting).content.imageURL !== undefined &&
        typeof (component as SlideComponentSasFillInTheBlanksWriting).content.imageURL === 'string' &&
        (component as SlideComponentSasFillInTheBlanksWriting).content.instructionText !== undefined &&
        typeof (component as SlideComponentSasFillInTheBlanksWriting).content.instructionText === 'string' &&
        (component as SlideComponentSasFillInTheBlanksWriting).content.extraAnswers !== undefined &&
        Array.isArray((component as SlideComponentSasFillInTheBlanksWriting).content.extraAnswers) &&
        (component as SlideComponentSasFillInTheBlanksWriting).content.extraAnswers.every((extraAnswer) => typeof extraAnswer === 'string') &&
        (component as SlideComponentSasFillInTheBlanksWriting).content.locale !== undefined &&
        typeof (component as SlideComponentSasFillInTheBlanksWriting).content.locale === 'string' &&
        (component as SlideComponentSasFillInTheBlanksWriting).content.flip !== undefined &&
        typeof (component as SlideComponentSasFillInTheBlanksWriting).content.flip === 'number'
    );
}

/**
 * end of SasFillInTheBlanksWriting (Legacy)
 */

/**
 * SasRadicalMatching (Legacy)
 */

export type SlideComponentSasRadicalMatchingAnswerModels = {
    correctAnswerPosition: 0 | 1 | 2; // 0: 錯, 1: 左正確, 2: 右正確
    text: string;
    urlPath: string;
};

export interface SlideComponentSasRadicalMatching extends SlideComponent {
    content: {
        layout: number;
        answerPosition: number;
        characterList: SlideComponentCharacterModel[];
        character: number;
        characterSound: SlideComponentCharacterSoundModel;
        phrase: string;
        phraseSound: SlideComponentCharacterSoundModel;
        phraseSoundGroup: SlideComponentCharacterSoundGroupModel;
        image: string;
        imageURL: string;
        flip: number;
        questionImageURL1: string;
        questionImageURL2: string;
        answers: SlideComponentSasRadicalMatchingAnswerModels[];
    };
}

export function isSlideComponentSasRadicalMatching(component: unknown): component is SlideComponentSasRadicalMatching {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'sasRadicalMatching' &&
        (component as SlideComponentSasRadicalMatching).content !== undefined &&
        (component as SlideComponentSasRadicalMatching).content.layout !== undefined &&
        typeof (component as SlideComponentSasRadicalMatching).content.layout === 'number' &&
        (component as SlideComponentSasRadicalMatching).content.answerPosition !== undefined &&
        typeof (component as SlideComponentSasRadicalMatching).content.answerPosition === 'number' &&
        (component as SlideComponentSasRadicalMatching).content.character !== undefined &&
        typeof (component as SlideComponentSasRadicalMatching).content.character === 'number' &&
        (component as SlideComponentSasRadicalMatching).content.characterSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentSasRadicalMatching).content.characterSound) &&
        (component as SlideComponentSasRadicalMatching).content.phrase !== undefined &&
        typeof (component as SlideComponentSasRadicalMatching).content.phrase === 'string' &&
        (component as SlideComponentSasRadicalMatching).content.phraseSound !== undefined &&
        isSlideComponentCharacterSoundModel((component as SlideComponentSasRadicalMatching).content.phraseSound) &&
        ((component as SlideComponentSasRadicalMatching).content.phraseSoundGroup === undefined ||
            isSlideComponentCharacterSoundGroupModel((component as SlideComponentSasRadicalMatching).content.phraseSoundGroup)) &&
        (component as SlideComponentSasRadicalMatching).content.image !== undefined &&
        typeof (component as SlideComponentSasRadicalMatching).content.image === 'string' &&
        (component as SlideComponentSasRadicalMatching).content.imageURL !== undefined &&
        typeof (component as SlideComponentSasRadicalMatching).content.imageURL === 'string' &&
        (component as SlideComponentSasRadicalMatching).content.questionImageURL1 !== undefined &&
        typeof (component as SlideComponentSasRadicalMatching).content.questionImageURL1 === 'string' &&
        (component as SlideComponentSasRadicalMatching).content.questionImageURL2 !== undefined &&
        typeof (component as SlideComponentSasRadicalMatching).content.questionImageURL2 === 'string' &&
        (component as SlideComponentSasRadicalMatching).content.flip !== undefined &&
        typeof (component as SlideComponentSasRadicalMatching).content.flip === 'number'
    );
}

/**
 * end of SasRadicalMatching (Legacy)
 */

/**
 * MultipleChoice (Legacy)
 */

export type SlideComponentMultipleChoiceQuestionModel = {
    id: string;
    answerIndex: number;
    questionText: string;
    choices: string[];
    pos: Coordinate;
};

export interface SlideComponentMultipleChoice extends SlideComponent {
    content: {
        questionTopic: string;
        questionLength: string;
        questions: SlideComponentMultipleChoiceQuestionModel[];
    };
}

export function isSlideComponentMultipleChoice(component: unknown): component is SlideComponentMultipleChoice {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'multipleChoice' &&
        (component as SlideComponentMultipleChoice).content !== undefined &&
        (component as SlideComponentMultipleChoice).content.questionTopic !== undefined &&
        typeof (component as SlideComponentMultipleChoice).content.questionTopic === 'string' &&
        (component as SlideComponentMultipleChoice).content.questionLength !== undefined &&
        typeof (component as SlideComponentMultipleChoice).content.questionLength === 'string'
    );
}

/**
 * end of MultipleChoice (Legacy)
 */

/**
 * Drag and Drop
 */

export type SlideComponentDndDragZone = SlideComponentDndDragZoneDefault | SlideComponentDndDragZoneText | SlideComponentDndDragZoneImage;

export interface SlideComponentDndDragZoneDefault {
    id: string;
    type: 'default';
    pos: Coordinate;
    size: Size;
    settings: {
        backgroundColor: string;
    };
    tag: string;
    content?: unknown;
}
export interface SlideComponentDndDragZoneText {
    id: string;
    type: 'text';
    pos: Coordinate;
    size: Size;
    settings: {
        backgroundColor: string;
    };
    tag: string;
    content: SlideComponentText['content'];
}
export interface SlideComponentDndDragZoneImage {
    id: string;
    type: 'image';
    pos: Coordinate;
    size: Size;
    settings: {
        backgroundColor: string;
    };
    tag: string;
    content: SlideComponentImage['content'];
}

export type SlideComponentDndDropZone = SlideComponentDndDropZoneDefault | SlideComponentDndDropZoneText | SlideComponentDndDropZoneImage;
export interface SlideComponentDndDropZoneDefault {
    id: string;
    type: 'default';
    pos: Coordinate;
    size: Size;
    settings: {
        multiple: boolean;
        backgroundColor: string;
    };
    tag: string;
    connectIds: string[];
    content?: unknown;
}
export interface SlideComponentDndDropZoneText {
    id: string;
    type: 'text';
    pos: Coordinate;
    size: Size;
    settings: {
        multiple: boolean;
        backgroundColor: string;
    };
    tag: string;
    connectIds: string[];
    content: SlideComponentText['content'];
}
export interface SlideComponentDndDropZoneImage {
    id: string;
    type: 'image';
    pos: Coordinate;
    size: Size;
    settings: {
        multiple: boolean;
        backgroundColor: string;
    };
    tag: string;
    connectIds: string[];
    content: SlideComponentImage['content'];
}

export interface SlideComponentDnd extends SlideComponent {
    content: {
        settings: { reusable: boolean };
        dragZone: SlideComponentDndDragZone[];
        dropZone: SlideComponentDndDropZone[];
    };
}

export function isSlideComponentDnd(component: unknown): component is SlideComponentDnd {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'dnd' &&
        (component as SlideComponentDnd).content !== undefined &&
        (component as SlideComponentDnd).content.settings !== undefined &&
        typeof (component as SlideComponentDnd).content.settings.reusable === 'boolean'
    );
}

export function isSlideComponentDndDragZone(component: unknown): component is SlideComponentDndDragZone {
    return (
        (component as SlideComponentDndDragZone).id !== undefined &&
        (component as SlideComponentDndDragZone).settings !== undefined &&
        (component as SlideComponentDndDragZone).settings.backgroundColor !== undefined &&
        typeof (component as SlideComponentDndDragZone).settings.backgroundColor === 'string' &&
        (component as SlideComponentDndDragZone).pos !== undefined &&
        isCoordinate((component as SlideComponentDndDragZone).pos) &&
        (component as SlideComponentDndDragZone).size !== undefined &&
        isSize((component as SlideComponentDndDragZone).size) &&
        (((component as SlideComponentDndDragZone).type === 'text' && (component as SlideComponentDndDragZone).content !== undefined && isSlideComponentText(component as SlideComponentDndDragZone)) ||
            ((component as SlideComponentDndDragZone).type === 'image' &&
                (component as SlideComponentDndDragZone).content !== undefined &&
                isSlideComponentImage(component as SlideComponentDndDragZone)) ||
            (component as SlideComponentDndDragZone).type === 'default')
    );
}

export function isSlideComponentDndDropZone(component: unknown): component is SlideComponentDndDropZone {
    return (
        (component as SlideComponentDndDropZone).id !== undefined &&
        (component as SlideComponentDndDropZone).settings !== undefined &&
        (component as SlideComponentDndDropZone).settings.backgroundColor !== undefined &&
        typeof (component as SlideComponentDndDropZone).settings.backgroundColor === 'string' &&
        (component as SlideComponentDndDropZone).settings.multiple !== undefined &&
        typeof (component as SlideComponentDndDropZone).settings.multiple === 'boolean' &&
        (component as SlideComponentDndDropZone).pos !== undefined &&
        isCoordinate((component as SlideComponentDndDropZone).pos) &&
        (component as SlideComponentDndDropZone).size !== undefined &&
        isSize((component as SlideComponentDndDropZone).size) &&
        (((component as SlideComponentDndDropZone).type === 'text' && (component as SlideComponentDndDropZone).content !== undefined && isSlideComponentText(component as SlideComponentDndDropZone)) ||
            ((component as SlideComponentDndDropZone).type === 'image' &&
                (component as SlideComponentDndDropZone).content !== undefined &&
                isSlideComponentImage(component as SlideComponentDndDropZone)) ||
            (component as SlideComponentDndDropZone).type === 'default')
    );
}

/**
 * end of Drag and Drop
 */

/**
 * MC
 */

export type SlideComponentMCAnswer = SlideComponentMCText | SlideComponentMCImage;

export interface SlideComponentMCText {
    id: string;
    type: 'text';
    pos: Coordinate;
    size: Size;
    settings: {
        backgroundColor: string;
        isAnswer: boolean;
    };
    content: SlideComponentText['content'];
}
export interface SlideComponentMCImage {
    id: string;
    type: 'image';
    pos: Coordinate;
    size: Size;
    settings: {
        backgroundColor: string;
        isAnswer: boolean;
    };
    content: SlideComponentImage['content'];
}

export interface SlideComponentMC extends SlideComponent {
    content: {
        answers: SlideComponentMCAnswer[];
    };
}

export function isSlideComponentMC(component: unknown): component is SlideComponentMC {
    return isSlideComponent(component) && (component as SlideComponent).type === 'mc' && (component as SlideComponentMC).content !== undefined;
}

export function isSlideComponentMCAnswer(component: unknown): component is SlideComponentMCAnswer {
    return (
        (component as SlideComponentMCAnswer).id !== undefined &&
        (component as SlideComponentMCAnswer).settings !== undefined &&
        (component as SlideComponentMCAnswer).settings.backgroundColor !== undefined &&
        typeof (component as SlideComponentMCAnswer).settings.backgroundColor === 'string' &&
        (component as SlideComponentMCAnswer).pos !== undefined &&
        isCoordinate((component as SlideComponentMCAnswer).pos) &&
        (component as SlideComponentMCAnswer).size !== undefined &&
        isSize((component as SlideComponentMCAnswer).size) &&
        (((component as SlideComponentMCAnswer).type === 'text' && (component as SlideComponentMCAnswer).content !== undefined && isSlideComponentText(component as SlideComponentMCAnswer)) ||
            ((component as SlideComponentMCAnswer).type === 'image' && (component as SlideComponentMCAnswer).content !== undefined && isSlideComponentImage(component as SlideComponentMCAnswer)))
    );
}

/**
 * end of MC
 */

/**
 * Character Writing
 */
export interface SlideComponentCharacterWriting extends SlideComponent {
    content: {
        code: string;
        locale: string;
        character: string;
    };
}

export function isSlideComponentCharacterWriting(component: unknown): component is SlideComponentCharacterWriting {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'characterWriting' &&
        (component as SlideComponentCharacterWriting).content !== undefined &&
        (component as SlideComponentCharacterWriting).content.code !== undefined &&
        typeof (component as SlideComponentCharacterWriting).content.code === 'string' &&
        (component as SlideComponentCharacterWriting).content.locale !== undefined &&
        typeof (component as SlideComponentCharacterWriting).content.locale === 'string' &&
        (component as SlideComponentCharacterWriting).content.character !== undefined &&
        typeof (component as SlideComponentCharacterWriting).content.character === 'string'
    );
}

/**
 * end of Character Writing
 */

/**
 * Character Cards
 */

export type CharacterCardsGameType = 'First' | 'Second' | 'Both';
export interface SlideComponentCharacterCards extends SlideComponent {
    content: {
        code: string;
        locale: string;
        character: string;
        cardType: number;
        serial1: string[];
        serial2: string[];
        settings: {
            gameType: CharacterCardsGameType;
        };
    };
}

export function isSlideComponentCharacterCards(component: unknown): component is SlideComponentCharacterCards {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'characterCards' &&
        (component as SlideComponentCharacterCards).content !== undefined &&
        (component as SlideComponentCharacterCards).content.code !== undefined &&
        typeof (component as SlideComponentCharacterCards).content.code === 'string' &&
        (component as SlideComponentCharacterCards).content.locale !== undefined &&
        typeof (component as SlideComponentCharacterCards).content.locale === 'string' &&
        (component as SlideComponentCharacterCards).content.character !== undefined &&
        typeof (component as SlideComponentCharacterCards).content.character === 'string' &&
        (component as SlideComponentCharacterCards).content.cardType !== undefined &&
        typeof (component as SlideComponentCharacterCards).content.cardType === 'number' &&
        (component as SlideComponentCharacterCards).content.serial1 !== undefined &&
        Array.isArray((component as SlideComponentCharacterCards).content.serial1) &&
        (component as SlideComponentCharacterCards).content.serial1.every((serial1) => typeof serial1 === 'string') &&
        (component as SlideComponentCharacterCards).content.serial2 !== undefined &&
        Array.isArray((component as SlideComponentCharacterCards).content.serial2) &&
        (component as SlideComponentCharacterCards).content.serial2.every((serial2) => typeof serial2 === 'string') &&
        (component as SlideComponentCharacterCards).content.settings !== undefined &&
        (component as SlideComponentCharacterCards).content.settings.gameType !== undefined &&
        ((component as SlideComponentCharacterCards).content.settings.gameType === 'First' ||
            (component as SlideComponentCharacterCards).content.settings.gameType === 'Second' ||
            (component as SlideComponentCharacterCards).content.settings.gameType === 'Both')
    );
}

/**
 * end of Character Cards
 */

/**
 * Drawing
 */
export interface SlideComponentDrawing extends SlideComponent {
    content: {
        image: string;
        imageURL: string;
        flip: number;
    };
}

export function isSlideComponentDrawing(component: unknown): component is SlideComponentDrawing {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'drawing' &&
        (component as SlideComponentDrawing).content !== undefined &&
        (component as SlideComponentDrawing).content.image !== undefined &&
        typeof (component as SlideComponentDrawing).content.image === 'string' &&
        (component as SlideComponentDrawing).content.imageURL !== undefined &&
        typeof (component as SlideComponentDrawing).content.imageURL === 'string' &&
        (component as SlideComponentDrawing).content.flip !== undefined &&
        typeof (component as SlideComponentDrawing).content.flip === 'number'
    );
}

/**
 * end of Drawing
 */

/**
 * SentenceMaker
 */
export interface SlideComponentSentenceMaker extends SlideComponent {
    content: {
        size: { cols: number; rows: number };
        maxWords: number;
        difficulty: string;
        language: 'traditional' | 'simplified';
        keywords: string[];
        sentencePattern: string;
        theme: string;
        writingTechnique: string;
        rhetoric: string;
    };
}

export function isSlideComponentSentenceMaker(component: unknown): component is SlideComponentSentenceMaker {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'sentenceMaker' &&
        (component as SlideComponentSentenceMaker).content !== undefined &&
        (component as SlideComponentSentenceMaker).content.size !== undefined &&
        (component as SlideComponentSentenceMaker).content.size.cols !== undefined &&
        typeof (component as SlideComponentSentenceMaker).content.size.cols === 'number' &&
        (component as SlideComponentSentenceMaker).content.size.rows !== undefined &&
        typeof (component as SlideComponentSentenceMaker).content.size.rows === 'number' &&
        (component as SlideComponentSentenceMaker).content.maxWords !== undefined &&
        typeof (component as SlideComponentSentenceMaker).content.maxWords === 'number' &&
        (component as SlideComponentSentenceMaker).content.difficulty !== undefined &&
        typeof (component as SlideComponentSentenceMaker).content.difficulty === 'string' &&
        (component as SlideComponentSentenceMaker).content.language !== undefined &&
        typeof (component as SlideComponentSentenceMaker).content.language === 'string' &&
        (component as SlideComponentSentenceMaker).content.keywords !== undefined &&
        Array.isArray((component as SlideComponentSentenceMaker).content.keywords) &&
        (component as SlideComponentSentenceMaker).content.keywords.every((keyword) => typeof keyword === 'string') &&
        (component as SlideComponentSentenceMaker).content.sentencePattern !== undefined &&
        typeof (component as SlideComponentSentenceMaker).content.sentencePattern === 'string' &&
        (component as SlideComponentSentenceMaker).content.theme !== undefined &&
        typeof (component as SlideComponentSentenceMaker).content.theme === 'string' &&
        (component as SlideComponentSentenceMaker).content.writingTechnique !== undefined &&
        typeof (component as SlideComponentSentenceMaker).content.writingTechnique === 'string' &&
        (component as SlideComponentSentenceMaker).content.rhetoric !== undefined &&
        typeof (component as SlideComponentSentenceMaker).content.rhetoric === 'string'
    );
}

/**
 * end of SentenceMaker
 */

/**
 * Audio Record
 */
export interface SlideComponentAudioRecord extends SlideComponent {
    content: object;
}

export function isSlideComponentAudioRecord(component: unknown): component is SlideComponentAudioRecord {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'audioRecord' &&
        (component as SlideComponentAudioRecord).content !== undefined &&
        (component as SlideComponentAudioRecord).content !== null
    );
}

/**
 * end of Audio Record
 */

/**
 * Pronunciation Assessment
 */
export interface SlideComponentPronunciationAssessment extends SlideComponent {
    content: {
        refText?: string;
    };
}

export function isSlideComponentPronunciationAssessment(component: unknown): component is SlideComponentPronunciationAssessment {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'pronunciationAssessment' &&
        (component as SlideComponentPronunciationAssessment).content !== undefined &&
        (component as SlideComponentPronunciationAssessment).content !== null &&
        ((component as SlideComponentPronunciationAssessment).content.refText === undefined || typeof (component as SlideComponentPronunciationAssessment).content.refText === 'string')
    );
}

/**
 * end of Pronunciation Assessment
 */

/**
 * Stroke Writing
 */
export interface SlideComponentStrokeWriting extends SlideComponent {
    content: {
        code: string;
        locale: string;
        character: string;
    };
}

export function isSlideComponentStrokeWriting(component: unknown): component is SlideComponentStrokeWriting {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'strokeWriting' &&
        (component as SlideComponentStrokeWriting).content !== undefined &&
        (component as SlideComponentStrokeWriting).content.code !== undefined &&
        typeof (component as SlideComponentStrokeWriting).content.code === 'string' &&
        (component as SlideComponentStrokeWriting).content.locale !== undefined &&
        typeof (component as SlideComponentStrokeWriting).content.locale === 'string' &&
        (component as SlideComponentStrokeWriting).content.character !== undefined &&
        typeof (component as SlideComponentStrokeWriting).content.character === 'string'
    );
}

/**
 * end of Stroke Writing
 */

/**
 * Radical Writing
 */
export interface SlideComponentRadicalWriting extends SlideComponent {
    content: {
        code: string;
        locale: string;
        character: string;
    };
}

export function isSlideComponentRadicalWriting(component: unknown): component is SlideComponentRadicalWriting {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'radicalWriting' &&
        (component as SlideComponentRadicalWriting).content !== undefined &&
        (component as SlideComponentRadicalWriting).content.code !== undefined &&
        typeof (component as SlideComponentRadicalWriting).content.code === 'string' &&
        (component as SlideComponentRadicalWriting).content.locale !== undefined &&
        typeof (component as SlideComponentRadicalWriting).content.locale === 'string' &&
        (component as SlideComponentRadicalWriting).content.character !== undefined &&
        typeof (component as SlideComponentRadicalWriting).content.character === 'string'
    );
}

/**
 * end of Radical Writing
 */

/**
 * Link
 */

export interface SlideComponentLink extends SlideComponent {
    content: {
        label: SlideComponentText;
        link: string;
    };
}

export function isSlideComponentLink(component: unknown): component is SlideComponentLink {
    return (
        isSlideComponent(component) &&
        (component as SlideComponent).type === 'link' &&
        (component as SlideComponentLink).content !== undefined &&
        (component as SlideComponentLink).content.label.type === 'text' &&
        (component as SlideComponentLink).content.label !== undefined &&
        isSlideComponentText((component as SlideComponentLink).content.label) &&
        (component as SlideComponentLink).content.link !== undefined &&
        typeof (component as SlideComponentLink).content.link === 'string'
    );
}

/**
 * end of Link
 */

/**
 * utils
 */

export function isQuestionComponentType(component: unknown): component is SlideComponent {
    return isSlideComponent(component) && (questionComponentType.some((t) => t === component.type) || legacyQuestionComponentType.some((t) => t === component.type));
}

/**
 * end of utils
 */
